import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail, ArrowLeft, Lock, FileText } from 'lucide-react';

const Section = ({ title, id, children }) => (
  <section id={id} className="scroll-mt-24 mb-10">
    <h2 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-3">
      <span className="w-1.5 h-7 bg-yellow-400 rounded-full"></span>
      {title}
    </h2>
    <div className="text-gray-300 text-sm md:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const PrivacyPolicy = () => {
  const lastUpdated = '17 de dezembro de 2025';

  const toc = [
    { id: 'introducao', label: '1. Introdução' },
    { id: 'controlador', label: '2. Controlador dos Dados' },
    { id: 'dados-coletados', label: '3. Dados Coletados' },
    { id: 'finalidades', label: '4. Finalidades do Tratamento' },
    { id: 'base-legal', label: '5. Base Legal' },
    { id: 'compartilhamento', label: '6. Compartilhamento de Dados' },
    { id: 'armazenamento', label: '7. Armazenamento e Segurança' },
    { id: 'cookies', label: '8. Cookies' },
    { id: 'direitos', label: '9. Direitos do Titular' },
    { id: 'retencao', label: '10. Retenção dos Dados' },
    { id: 'menores', label: '11. Crianças e Adolescentes' },
    { id: 'alteracoes', label: '12. Alterações desta Política' },
    { id: 'contato', label: '13. Contato / DPO' },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header Banner */}
      <div className="relative overflow-hidden bg-gradient-to-b from-yellow-500/10 to-transparent border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none"></div>
        <div className="container mx-auto px-4 py-12 md:py-16 relative z-10">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-yellow-400 text-sm mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>

          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 md:w-8 md:h-8 text-yellow-400" />
            </div>
            <div>
              <p className="text-yellow-400 text-xs md:text-sm font-semibold tracking-widest uppercase">
                LGPD · Lei nº 13.709/2018
              </p>
              <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mt-1">
                Política de Privacidade
              </h1>
            </div>
          </div>

          <p className="text-gray-400 text-sm md:text-base max-w-3xl">
            Esta política descreve como a <strong className="text-white">WING ACADEMY BRASIL</strong> coleta,
            utiliza, armazena e protege seus dados pessoais, em conformidade com a
            <strong className="text-white"> Lei Geral de Proteção de Dados Pessoais (LGPD)</strong>.
          </p>
          <p className="text-gray-500 text-xs md:text-sm mt-3">
            Última atualização: {lastUpdated}
          </p>
        </div>
      </div>

      {/* Content + TOC */}
      <div className="container mx-auto px-4 py-10 md:py-14">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8 md:gap-12 max-w-6xl mx-auto">
          {/* TOC */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="glass-dark border border-white/5 rounded-2xl p-5">
              <p className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Sumário
              </p>
              <ul className="space-y-1.5">
                {toc.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="block text-gray-300 hover:text-yellow-400 text-sm py-1 transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <article data-testid="privacy-content" className="glass-dark border border-white/5 rounded-2xl p-6 md:p-10">
            <Section title="1. Introdução" id="introducao">
              <p>
                A WING ACADEMY BRASIL ("nós", "nosso" ou "Plataforma") tem o compromisso de proteger a
                privacidade e os dados pessoais dos seus usuários, alunos e visitantes ("você" ou "Titular"),
                em conformidade com a Lei nº 13.709/2018 (LGPD).
              </p>
              <p>
                Ao utilizar nosso site, preencher formulários de captura de leads, realizar o quiz ou
                contratar nossos serviços de mentoria, você concorda com os termos descritos nesta política.
              </p>
            </Section>

            <Section title="2. Controlador dos Dados" id="controlador">
              <p>
                Para os fins da LGPD, o <strong className="text-white">Controlador</strong> dos dados pessoais
                coletados através deste site é:
              </p>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 not-italic">
                <p><strong className="text-white">WING ACADEMY BRASIL</strong></p>
                <p>E-mail: wingacademybrasil@gmail.com</p>
                <p>WhatsApp: (21) 99956-8656</p>
              </div>
            </Section>

            <Section title="3. Dados Pessoais que Coletamos" id="dados-coletados">
              <p>Coletamos apenas os dados necessários para prestar nossos serviços:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong className="text-white">Dados cadastrais:</strong> nome completo, e-mail, telefone/WhatsApp, estado.</li>
                <li><strong className="text-white">Dados de formulário / quiz:</strong> nível de experiência, respostas do quiz, pontuação.</li>
                <li><strong className="text-white">Dados de navegação:</strong> endereço IP, navegador, páginas visitadas, origem do acesso (cookies).</li>
                <li><strong className="text-white">Dados de comunicação:</strong> mensagens trocadas por e-mail, WhatsApp ou formulário.</li>
              </ul>
              <p className="text-yellow-400/90 text-sm">
                Não solicitamos nem armazenamos dados bancários, CPF ou documentos de identidade através deste site.
                Pagamentos são processados por meio de parceiros (ex.: Mercado Pago), que possuem suas próprias políticas.
              </p>
            </Section>

            <Section title="4. Finalidades do Tratamento" id="finalidades">
              <p>Seus dados são utilizados para:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Entrar em contato para informar sobre turmas, condições e próximos passos da mentoria.</li>
                <li>Enviar materiais educativos, dicas técnicas e comunicações relacionadas ao curso.</li>
                <li>Gerir sua participação no quiz e comunicar resultados.</li>
                <li>Gerar indicadores internos (estatísticas e relatórios) sobre o desempenho do site.</li>
                <li>Cumprir obrigações legais e regulatórias.</li>
              </ul>
            </Section>

            <Section title="5. Base Legal" id="base-legal">
              <p>O tratamento dos seus dados fundamenta-se em:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong className="text-white">Consentimento</strong> (art. 7º, I da LGPD), para envio de comunicações e captura de leads.</li>
                <li><strong className="text-white">Execução de contrato</strong> (art. 7º, V), para prestação dos serviços contratados.</li>
                <li><strong className="text-white">Legítimo interesse</strong> (art. 7º, IX), para análise estatística e segurança do site.</li>
                <li><strong className="text-white">Cumprimento de obrigação legal</strong> (art. 7º, II).</li>
              </ul>
            </Section>

            <Section title="6. Compartilhamento de Dados" id="compartilhamento">
              <p>
                A WING ACADEMY BRASIL <strong className="text-white">não comercializa</strong> dados pessoais.
                O compartilhamento ocorre apenas com parceiros operacionais estritamente necessários, como:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Plataformas de hospedagem e armazenamento em nuvem.</li>
                <li>Processador de pagamento (Mercado Pago) quando aplicável.</li>
                <li>Ferramentas de envio de e-mail e mensageria (WhatsApp).</li>
                <li>Autoridades públicas, quando exigido por lei.</li>
              </ul>
              <p>Todos os parceiros são obrigados contratualmente a respeitar a LGPD.</p>
            </Section>

            <Section title="7. Armazenamento e Segurança" id="armazenamento">
              <p>
                Adotamos medidas técnicas e administrativas razoáveis para proteger seus dados contra
                acessos não autorizados, perda, alteração ou divulgação indevida, incluindo:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Criptografia em trânsito (HTTPS/TLS).</li>
                <li>Controle de acesso autenticado no painel administrativo.</li>
                <li>Senhas armazenadas com hash bcrypt (não reversível).</li>
                <li>Backups regulares e ambientes segregados.</li>
              </ul>
              <p className="text-gray-400 text-sm">
                Apesar de adotarmos as melhores práticas, nenhum sistema é 100% invulnerável. Em caso de
                incidente envolvendo dados pessoais, comunicaremos os titulares afetados e a ANPD conforme exigido.
              </p>
            </Section>

            <Section title="8. Cookies e Tecnologias Semelhantes" id="cookies">
              <p>
                Nosso site pode utilizar cookies para melhorar sua experiência, lembrar preferências e gerar
                estatísticas de uso anônimas. Você pode configurar seu navegador para bloquear ou alertar
                sobre cookies a qualquer momento.
              </p>
            </Section>

            <Section title="9. Direitos do Titular" id="direitos">
              <p>
                Conforme o art. 18 da LGPD, você, como titular dos dados, tem direito a:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Confirmação da existência de tratamento.</li>
                <li>Acesso aos dados que mantemos sobre você.</li>
                <li>Correção de dados incompletos, inexatos ou desatualizados.</li>
                <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade.</li>
                <li>Portabilidade dos dados a outro fornecedor de serviço.</li>
                <li>Eliminação dos dados tratados com base em consentimento.</li>
                <li>Informação sobre compartilhamento.</li>
                <li>Revogação do consentimento.</li>
                <li>Oposição a tratamento em desconformidade com a LGPD.</li>
              </ul>
              <p>
                Para exercer qualquer direito, envie um e-mail para
                <a href="mailto:wingacademybrasil@gmail.com" className="text-yellow-400 hover:underline"> wingacademybrasil@gmail.com</a>.
                Responderemos em até <strong className="text-white">15 dias corridos</strong>.
              </p>
            </Section>

            <Section title="10. Retenção dos Dados" id="retencao">
              <p>
                Mantemos seus dados pelo tempo necessário para cumprir as finalidades descritas ou para
                atender obrigações legais. Após esse período, os dados serão anonimizados ou eliminados
                de forma segura.
              </p>
            </Section>

            <Section title="11. Crianças e Adolescentes" id="menores">
              <p>
                Nosso conteúdo é direcionado a adultos (18+). Não coletamos intencionalmente dados de
                menores de idade. Caso identifiquemos, os dados serão excluídos imediatamente.
              </p>
            </Section>

            <Section title="12. Alterações desta Política" id="alteracoes">
              <p>
                Esta Política pode ser atualizada a qualquer momento. A data da última revisão será sempre
                exibida no topo desta página. Recomendamos que você a consulte periodicamente.
              </p>
            </Section>

            <Section title="13. Contato e Encarregado (DPO)" id="contato">
              <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-400/30 rounded-xl p-5 not-italic">
                <p className="flex items-center gap-2 text-white font-bold mb-3">
                  <Mail className="w-5 h-5 text-yellow-400" />
                  Encarregado pelo Tratamento de Dados (DPO)
                </p>
                <p className="text-gray-300 text-sm">
                  Em caso de dúvidas, reclamações ou para exercer qualquer direito previsto na LGPD, entre em contato:
                </p>
                <ul className="text-sm text-gray-200 mt-3 space-y-1">
                  <li>📧 <a href="mailto:wingacademybrasil@gmail.com" className="text-yellow-400 hover:underline">wingacademybrasil@gmail.com</a></li>
                  <li>📱 WhatsApp: <a href="https://wa.me/5521999568656" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:underline">(21) 99956-8656</a></li>
                </ul>
              </div>
              <p className="text-gray-500 text-xs pt-2 flex items-center gap-2">
                <Lock className="w-3.5 h-3.5" />
                Seus dados estão seguros conosco.
              </p>
            </Section>

            {/* Back to top / home */}
            <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-yellow-400 text-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao início
              </Link>
              <a
                href="#introducao"
                className="text-gray-400 hover:text-yellow-400 text-sm transition-colors"
              >
                ↑ Voltar ao topo
              </a>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
